'use client';

/**
 * Ask Query Form Component
 *
 * A form component for users to ask questions or report issues.
 * This is a pure form component without built-in mobile drawer functionality.
 */
import { zodResolver } from '@hookform/resolvers/zod';
// Assuming sonner is installed/available
import { Plus, Upload, X } from 'lucide-react';
import * as React from 'react';
import { toast } from 'sonner';
import { z } from 'zod';

import { cn } from '../../../../lib/utils';
// Corrected import path
import { Form, FormField, FormItem, FormLabel, FormMessage, useFormBuilder } from '../../../common/form';
import { FloatingInput } from '../../../common/input';
import { FloatingTextArea } from '../../../common/textarea';
import { Button } from '../../../ui/button';
import {
  FileUpload,
  FileUploadDropzone,
  FileUploadItem,
  FileUploadItemDelete,
  FileUploadItemMetadata,
  FileUploadItemPreview,
  FileUploadList,
  FileUploadTrigger
} from '../../../ui/file-upload';

// Define constants locally instead of importing from @toolkit/constants
const MAX_ATTACHMENT_SIZE = 10 * 1024 * 1024; // 10MB in bytes
const MAX_ATTACHMENTS = 6; // Maximum number of attachments allowed

// Adjusted import path

// Define the form schema with validation
// Remove attachment from form values, handle separately
// Email validation is conditional - required for guests, optional for logged-in users
const createAskQueryFormSchema = (
  userEmail?: string,
  errorMessages?: { emailError?: string; questionError?: string }
) =>
  z.object({
    email: userEmail
      ? z.string().optional() // Not required if user is logged in
      : z.string().email({ message: errorMessages?.emailError || 'Please enter a valid email address.' }),
    question: z
      .string()
      .min(10, { message: errorMessages?.questionError || 'Question must be at least 10 characters.' })
  });

// Base type for form values (email can be string or undefined)
export type AskQueryFormValues = {
  email?: string;
  question: string;
};

export interface AskQueryFormProps {
  /**
   * Optional callback when form is submitted
   */
  onSubmit?: (data: AskQueryFormValues, attachments?: File[]) => void; // Updated signature

  /**
   * Optional class name for the container
   */
  className?: string;

  /**
   * Whether the form is in a loading state
   */
  isLoading?: boolean;

  /**
   * Optional callback when form is cancelled
   */
  onCancel?: () => void;

  /**
   * Max number of files allowed
   */
  maxFiles?: number;

  /**
   * Max file size in MB
   */
  maxSizeMB?: number;

  /**
   * User's email when logged in. When provided, the email field will be hidden
   * and this email will be used for the submission.
   */
  userEmail?: string;

  /**
   * Translated strings for the form
   */
  translations?: {
    title?: string;
    email?: string;
    emailPlaceholder?: string;
    question?: string;
    questionPlaceholder?: string;
    attachment?: string;
    attachmentPrompt?: string;
    // Split attachment info into parts
    attachmentInfoPrefix?: string;
    attachmentInfoSuffix?: string;
    attachmentInfoSuffix2?: string;
    // Split max images warning into parts
    maxImagesWarningPrefix?: string;
    maxImagesWarningSuffix?: string;
    submitButton?: string;
    submittingButton?: string;
    responseTime?: string;
    emailError?: string;
    questionError?: string;
  };
}

/**
 * Ask Query Form component for users to submit questions or issues
 */
export const AskQueryForm = ({
  onSubmit,
  className,
  isLoading = false,
  onCancel,
  maxFiles = MAX_ATTACHMENTS, // Default max files
  maxSizeMB = MAX_ATTACHMENT_SIZE / (1024 * 1024), // Convert bytes to MB
  userEmail,
  translations,
  ...props
}: AskQueryFormProps) => {
  // State for the selected files
  const [attachments, setAttachments] = React.useState<File[]>([]);

  // Create schema based on whether user is logged in
  const schema = React.useMemo(
    () =>
      createAskQueryFormSchema(userEmail, {
        emailError: translations?.emailError,
        questionError: translations?.questionError
      }),
    [userEmail, translations?.emailError, translations?.questionError]
  );

  // Initialize the form
  const form = useFormBuilder({
    defaultValues: {
      email: '',
      question: ''
      // attachments: [] // No longer needed in form state directly
    },
    resolver: zodResolver(schema),
    mode: 'onChange' // Validate on change to enable/disable submit button
  });

  // Handle form submission
  const handleSubmit = async (data: AskQueryFormValues) => {
    // Use userEmail if provided (logged-in user), otherwise use form email
    const submissionData: AskQueryFormValues = {
      ...data,
      email: userEmail || data.email
    };
    if (onSubmit) {
      onSubmit(submissionData, attachments); // Pass the array of files
    }
    console.log('Form submitted:', submissionData, attachments);
  };

  /* --- File Validation & toast helpers --- */
  const onFileValidate = React.useCallback(
    (file: File): string | null => {
      // Check file count limit
      if (attachments.length >= maxFiles) {
        return `Maximum ${maxFiles} images allowed`;
      }

      // Check file type (both MIME type and extension)
      const validTypes = ['image/jpeg', 'image/png'];
      const validExtensions = ['.jpg', '.jpeg', '.png'];
      const fileExt = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));

      if (!validTypes.includes(file.type) || !validExtensions.includes(fileExt)) {
        return 'Only JPG and PNG images are allowed';
      }

      // Check individual file size
      const MAX_SIZE = maxSizeMB * 1024 * 1024;
      if (file.size > MAX_SIZE) {
        return `Image must be smaller than ${maxSizeMB}MB`;
      }

      // Calculate total size of all attachments including this new one
      const totalSize = attachments.reduce((sum, file) => sum + file.size, 0) + file.size;
      const MAX_TOTAL_SIZE = maxSizeMB * maxFiles * 1024 * 1024;
      if (totalSize > MAX_TOTAL_SIZE) {
        return `Total size of all images cannot exceed ${maxSizeMB * maxFiles}MB`;
      }

      return null;
    },
    [attachments, maxFiles, maxSizeMB]
  );

  const onFileReject = React.useCallback((file: File, message: string) => {
    toast(message, {
      description: `"${file.name.length > 20 ? `${file.name.slice(0, 20)}...` : file.name}" was rejected`
    });
  }, []);

  return (
    <div className={cn('self-stretch flex flex-col justify-start items-center', className)} {...props}>
      {/* Drawer handle now handled by the Drawer component */}
      <div className="w-full flex flex-col justify-start items-start gap-6">
        <h3 className={cn('text-xl font-medium text-foreground')}>{translations?.title || 'Ask Query'}</h3>

        <Form form={form} onSubmit={handleSubmit} className="w-full space-y-4">
          {' '}
          {/* Added space-y-4 */}
          {/* Only show email field if user is not logged in (no userEmail provided) */}
          {!userEmail && (
            <FormField
              name="email"
              // control is implicitly handled by FormProvider and useFormContext in FloatingInput
              render={() => (
                <FloatingInput
                  name="email"
                  label={translations?.email || 'Mail ID'}
                  type="email"
                  placeholder={translations?.emailPlaceholder || 'Enter your email'}
                  required // Matches the zod schema for guest users
                  // Use default FloatingInput styles
                  containerClassName="self-stretch" // Apply container styles here
                />
              )}
            />
          )}
          <FormField
            name="question"
            // control is implicitly handled by FormProvider and useFormContext in FloatingTextArea
            render={() => (
              <FloatingTextArea
                name="question"
                label={translations?.question || "What's Your Question or issue?"}
                placeholder={translations?.questionPlaceholder || 'Type your question or issue here'}
                required // Matches the zod schema (min length implies required)
                containerClassName="self-stretch"
                textareaClassName="min-h-[120px]"
              />
            )}
          />
          {/* File Uploader Section */}
          <FormItem className="self-stretch">
            <FormLabel className="text-Colors-Neutral-700 text-sm font-normal font-['Manrope']">
              {translations?.attachment || 'Attachment'}
            </FormLabel>
            <FileUpload
              value={attachments}
              onValueChange={setAttachments}
              onFileValidate={onFileValidate}
              onFileReject={onFileReject}
              accept="image/jpeg,image/png"
              maxFiles={maxFiles}
              multiple
              className="w-full"
            >
              {/* Choose which UI to show based on attachment count */}
              {attachments.length === 0 ? (
                /* Initial Dropzone: Show only when no files are attached */
                <FileUploadDropzone className="rounded-2xl border-2 border-dashed p-6 text-center hover:bg-muted/10">
                  <div className="flex flex-col items-center gap-1">
                    <div className="flex items-center justify-center rounded-full border p-2.5">
                      <Upload className="size-6 text-muted-foreground" />
                    </div>
                    <p className="text-sm font-medium">
                      {translations?.attachmentPrompt || 'Attach image or screenshots'}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {translations?.attachmentInfoPrefix ? (
                        <>
                          {translations.attachmentInfoPrefix}
                          {maxSizeMB}
                          {translations.attachmentInfoSuffix || 'MB per image (up to '}
                          {maxFiles}
                          {translations.attachmentInfoSuffix2 || ' images)'}
                        </>
                      ) : (
                        `JPG, PNG | Max ${maxSizeMB}MB per image (up to ${maxFiles} images)`
                      )}
                    </p>
                  </div>
                  <FileUploadTrigger asChild>
                    {/* <Button variant="outline" size="sm" className="mt-3">
                    Browse files
                  </Button> */}
                  </FileUploadTrigger>
                </FileUploadDropzone>
              ) : (
                /* Previews & Add More Tile: Show when files are attached */
                <FileUploadList className="grid grid-cols-3 gap-3">
                  {attachments.map((file) => (
                    <FileUploadItem key={file.name} value={file} className="relative overflow-hidden rounded-2xl">
                      <FileUploadItemPreview className="aspect-square h-full w-full object-cover" />
                      <FileUploadItemMetadata className="hidden" />
                      <FileUploadItemDelete asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="absolute right-1/10 top-1/10 z-10 h-5 w-5 rounded-full bg-white/40 backdrop-blur"
                        >
                          <X className="h-5 w-5" />
                        </Button>
                      </FileUploadItemDelete>
                    </FileUploadItem>
                  ))}
                  {/* Add More Tile: Show only if below max file limit */}
                  {attachments.length < maxFiles && (
                    <FileUploadDropzone className="rounded-2xl grid place-items-center border-2 border-dashed p-6 text-center hover:bg-muted/10">
                      <Plus className="h-8 w-8 text-muted-foreground" />

                      <FileUploadTrigger asChild>
                        {/* <Button variant="outline" size="sm" className="mt-3">
                    Browse files
                  </Button> */}
                      </FileUploadTrigger>
                    </FileUploadDropzone>
                  )}
                </FileUploadList>
              )}

              {/* Message when max files reached - show below the grid */}
              {attachments.length >= maxFiles && (
                <div className="rounded-2xl text-center mt-4">
                  <p className="text-sm text-destructive">
                    {translations?.maxImagesWarningPrefix ? (
                      <>
                        {translations.maxImagesWarningPrefix}
                        {maxFiles}
                        {translations.maxImagesWarningSuffix || ' images allowed'}
                      </>
                    ) : (
                      `Maximum ${maxFiles} images allowed`
                    )}
                  </p>
                </div>
              )}
            </FileUpload>
            <FormMessage /> {/* Keep FormMessage for potential errors */}
          </FormItem>
          {/* End File Uploader Section */}
          <Button
            type="submit"
            disabled={isLoading || !form.formState.isValid}
            className={cn(
              "w-full self-stretch px-3 py-2 rounded-lg inline-flex justify-center items-center gap-1 text-sm font-semibold font-['Manrope'] leading-tight",
              isLoading || !form.formState.isValid
                ? 'bg-neutral-100 text-neutral-300'
                : 'bg-primary-400 hover:bg-primary-300 text-white'
            )}
          >
            {isLoading ? translations?.submittingButton || 'Sending...' : translations?.submitButton || 'Send Message'}
          </Button>
          {/* Footer Text */}
          <div className="text-center justify-start text-Colors-Neutral-600 text-xs font-normal font-['Manrope'] leading-none">
            {translations?.responseTime || 'Usually responds within 24 hours'}
          </div>
        </Form>
      </div>
    </div>
  );
};

export default AskQueryForm;
